/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import { AppAuthorizationRecommendedApps } from 'client/util/Constants';
import { cloneDeep } from 'lodash';
import CollectionModule from 'client/store/modules/CollectionModule';

export class AppAuthorizationCollectionClass extends CollectionModule {
  extractModule() {
    const module = super.extractModule();

    module.getters.getRecommendedApps = (...params) => this.getRecommendedApps(...params);
    module.getters.getSimpleAppList = (...params) => this.getSimpleAppList(...params);
    module.getters.getSearchField = (...params) => this.getSearchField(...params);
    module.getters.getSortedCollection = (...params) => this.getSortedCollection(...params);

    module.actions.addNewItem = (...params) => this.addNewItem(...params);
    module.actions.checkAppNameExists = (...params) => this.checkAppNameExists(...params);
    module.actions.updateItem = (...params) => this.updateItem(...params);
    module.actions.setCollectionToRecommendedApps = (...params) => this
      .setCollectionToRecommendedApps(...params);
    module.actions.setCollectionToCustomApps = (...params) => this
      .setCollectionToCustomApps(...params);
    module.actions.fetchCollection = (...params) => this.fetchCollection(...params);
    module.actions.deleteItems = (...params) => this.deleteItems(...params);
    module.actions.selectAllItems = (...params) => this.selectAllItems(...params);
    module.actions.setSortField = (...params) => this.setSortField(...params);
    module.actions.setSearchTerm = (...params) => this.setSearchTerm(...params);

    return module;
  }

  // ----------------------------------------------------------------------------------------------
  // Getters
  // ----------------------------------------------------------------------------------------------

  getRecommendedApps() {
    return AppAuthorizationRecommendedApps.list.map(item => ({
      id: item, name: item, path: '', source: AppAuthorizationRecommendedApps.columnLabel,
    }));
  }

  /**
   * Checks if the provided trustedApp is to be considered a recommended app
   * @param trustedApp
   * @returns {string}
   */
  getAppSource(trustedApp) {
    const isPathEmpty = trustedApp.path === '';
    const isNameARecommendedApp = AppAuthorizationRecommendedApps.list
      .find(item => item === trustedApp.name);

    return isPathEmpty && isNameARecommendedApp
      ? AppAuthorizationRecommendedApps.columnLabel
      : 'Custom';
  }

  // returns a list of apps without id or source attributes
  getSimpleAppList(context) {
    const trustedApps = this.getCollection(context.state);
    return trustedApps.map(item => ({ name: item.name, path: item.path, teamid: '' }));
  }

  /**
   * Sorts the collection by sort and searchTerm parameters if available.
   */
  getSortedCollection(context, currentCollection) {
    const { sort } = context.state.features.items;
    const { searchTerm } = context.state.features.items;

    if (sort) {
      if (sort.charAt(0) === '-') {
        const sortAttribute = sort.slice(1);
        currentCollection.sort(
          (first, second) => second[sortAttribute]
            .localeCompare(first[sortAttribute]),
        );
      } else {
        currentCollection.sort(
          (first, second) => first[sort]
            .localeCompare(second[sort]),
        );
      }
    }

    if (searchTerm) {
      const lowercaseSearchTerm = searchTerm.toLowerCase();
      const sortBySearchTerm = (first, second) => {
        const lowerCaseFirstName = first.name.toLowerCase();
        const lowerCaseSecondName = second.name.toLowerCase();

        if (lowerCaseFirstName.includes(lowercaseSearchTerm)) return -1;
        if (lowerCaseFirstName.includes(lowercaseSearchTerm)
          && lowerCaseSecondName.includes(lowercaseSearchTerm)) return 0;
        if (lowerCaseSecondName.includes(lowercaseSearchTerm)) return 1;
        return 0;
      };

      currentCollection.sort(sortBySearchTerm);
    }
    return currentCollection;
  }

  // override to avoid itemTableSearchBar errors
  getSearchField() {
    return () => 'name';
  }

  // ----------------------------------------------------------------------------------------------
  // Actions
  // ----------------------------------------------------------------------------------------------

  updateTrustedApps(context, trustedApps) {
    const totalCount = trustedApps.length;
    const items = {
      results: this.getSortedCollection(context, trustedApps),
      totalCount,
      totalCountOfAllItems: totalCount,
    };

    context.commit('RESET_COLLECTION', { soft: true });
    context.commit('SET_COLLECTION', items);

    return new Promise((resolve) => {
      resolve(this.getSimpleAppList(context));
    });
  }

  async setCollectionToRecommendedApps(context) {
    await context.commit('RESET_COLLECTION');

    const recommendedApps = this.getRecommendedApps();

    return this.updateTrustedApps(context, recommendedApps);
  }

  async setCollectionToCustomApps(context, customApps) {
    const mappedCustomApps = customApps.map(item => ({
      id: item.name,
      name: item.name,
      path: item.path,
      source: this.getAppSource(item),
    }));

    return this.updateTrustedApps(context, mappedCustomApps);
  }

  // eslint-disable-next-line no-unused-vars
  fetchCollection(context, { next = false } = {}) {
    return Promise.resolve();
  }

  // eslint-disable-next-line no-unused-vars
  refreshCollection(context, { next = false } = {}) {
    return Promise.resolve();
  }

  deleteItems(context, ids = []) {
    const deleteIds = cloneDeep(ids);
    context.commit('DELETE_ITEMS', deleteIds);
    return new Promise((resolve) => {
      resolve(this.getSimpleAppList(context));
    });
  }

  selectAllItems(context) {
    const { ids } = context.state.features.items;
    return new Promise((resolve) => {
      context.commit('SELECT_ITEMS', { ids });
      resolve();
    });
  }

  unselectAllItems(context) {
    const { ids } = context.state.features.items;
    return new Promise((resolve) => {
      context.commit('UNSELECT_ITEMS', { ids });
      resolve();
    });
  }

  // saves a new item to the collection
  addNewItem(context, item) {
    const customItem = {
      id: item.name,
      name: item.name,
      path: item.path,
      teamid: '',
      source: this.getAppSource(item),
    };

    const newItems = this.getCollection(context.state);
    newItems.push(customItem);

    return this.updateTrustedApps(context, newItems);
  }

  updateItem(context, item) {
    context.commit('DELETE_ITEMS', [item.id]);
    return this.addNewItem(context, item);
  }

  // checks if the name attribute of the provided item is already used by other trusted apps
  checkAppNameExists(context, item) {
    const currentApps = this.getCollection(context.state);
    const foundItem = currentApps.find(app => app.name === item.name);
    return !!(foundItem && foundItem.id !== item.id);
  }

  /**
   * Set the sort field and sorts the collection items keeping the current items.
   */
  setSortField(context, { sortField }) {
    const currentCollection = this.getCollection(context.state);
    super.setSortField(context, { sortField });

    this.updateTrustedApps(context, currentCollection);
  }

  /**
   * Sets the search term and sorts the collection by search term
   */
  setSearchTerm(context, { searchTerm, preventReset }) {
    const currentCollection = this.getCollection(context.state);
    super.setSearchTerm(context, { searchTerm, preventReset });

    this.updateTrustedApps(context, currentCollection);
  }

  // ----------------------------------------------------------------------------------------------
  // Mutations
  // ----------------------------------------------------------------------------------------------
}

export default new AppAuthorizationCollectionClass({}, 'name', 'name').extractModule();
